import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {MachineService} from '@services/administration/machine/machine.service';
import {TranslateService} from '@ngx-translate/core';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {NotificationService} from '@services/notification/notification.service';
import {Machine} from '@models/machine/machine';
import {Signal} from '@models/dashboard/signal';
import {SortingService} from '@services/administration/sorting/sorting.service';
import {EnvironmentConfig} from '@environments/environment';
import {MachineMenuItems} from '@constants/administration/machine/machine-menu-items';
import {MachineTagService} from '@services/administration/machine/tag/machine-tag.service';
import {MachineTag} from '@models/adminstration/machine/tag/machine-tag';
import {Role} from '@models/user/role';
import {User} from '@models/user/user';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {utob} from '@prototypes/global.functions';
import {ELASTIC_SEARCH_TYPE} from '@constants/elastic-search/elastic-search-type.enum';

@Component({
  selector: 'scitis-admin-machine-configuration',
  templateUrl: './machine-configuration.component.html',
  styleUrls: ['./machine-configuration.component.scss'],
  standalone: false
})
export class MachineConfigurationComponent implements OnInit, OnDestroy {

  machineName: string = '';
  show: MachineMenuItems = MachineMenuItems.DATA_CONFIG;
  machineId: number = -1;

  userRole: Role = new Role(new User());
  isDistributorOrLower = true;
  tenant: number | undefined = undefined;
  machine: Machine = new Machine();
  machineTag: MachineTag | null = null;
  calculatedSignals: Signal[] = [];
  unCalculatedSignals: Signal[] = [];
  statusSignals: Signal[] = [];
  allSignals: Signal[] = [];
  isLoadingMachineDetails = true;
  externalApiEnabled = false;
  menuItems = MachineMenuItems;
  public searchTerm: string = '';


  private CURRENT_PAGE_NAME = 'MachineConfiguration';
  private destroy: Subject<boolean> = new Subject<boolean>();

  constructor(private activatedRoute: ActivatedRoute,
              private machineService: MachineService,
              private translate: TranslateService,
              private loadingIndicator: LoadingIndicatorService,
              private notification: NotificationService,
              private sortingService: SortingService,
              private router: Router,
              private machineTagService: MachineTagService) {
    this.searchTerm = this.router.currentNavigation()?.extras.state?.search;
    this.externalApiEnabled = EnvironmentConfig.PortalSettings.externalApiEnabled;
  }

  private static filterCalculatedSignals(unfilteredSignals: Signal[]): Signal[] {
    return unfilteredSignals.filter(signal => signal.calculated && !signal.isStatusSignal);
  }

  private static filterUncalculatedSignals(unfilteredSignals: Signal[]): Signal[] {
    return unfilteredSignals.filter(signal => !signal.calculated);
  }

  private static filterStatusSignals(unfilteredSignals: Signal[]): Signal[] {
    return unfilteredSignals.filter(signal => signal.isStatusSignal);
  }

  ngOnInit(): void {
    this.isLoadingMachineDetails = true;
    this.loadingIndicator.show(this.translate.instant('Loading machine configuration'));
    this.getMachineDetailsFromUrl();
    this.isDistributorOrLower = this.userRole.isDistributorRole || this.userRole.isAnalyticsRole || this.userRole.isUserRole || this.userRole.isViewerRole;
    const defaultPage = this.isDistributorOrLower ? MachineMenuItems.TRIGGER_ACTION : MachineMenuItems.DATA_CONFIG;
    this.activatedRoute.queryParams
    .pipe(takeUntil(this.destroy))
    .subscribe(params => this.show = params.page ?? defaultPage);
  }

  onNavChange(event: any) {
    this.show = event.nextId;
    this.router.navigate([], {relativeTo: this.activatedRoute, queryParams: {page: this.show}});
  }

  onMenuClick(val: MachineMenuItems) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {page: val},
    });
  }

  private getMachineDetailsFromUrl(): void {
    const details = this.activatedRoute.snapshot.paramMap.get('details');
    if (!details) {
      this.isLoadingMachineDetails = false;
      this.loadingIndicator.hide();
      return;
    }
    const parsed = JSON.parse(utob(details));
    this.machineId = parsed.id;
    this.userRole = parsed.userRole;
    this.tenant = parsed.tenant;
    this.setMachineConfiguration();
  }

  private setMachineConfiguration(): void {
    this.machineService.getMachineConfiguration(this.machineId).pipe(takeUntil(this.destroy)).subscribe({
      next: (machine: Machine) => {
        this.machine = machine;
        this.getMachineTagDetails(machine.category, machine.machineTag);
        this.machineName = machine.type;
        this.allSignals = this.loadSignalsData(machine.signals);
        this.calculatedSignals = MachineConfigurationComponent.filterCalculatedSignals(this.allSignals);
        this.unCalculatedSignals = MachineConfigurationComponent.filterUncalculatedSignals(this.allSignals);
        this.statusSignals = MachineConfigurationComponent.filterStatusSignals(this.allSignals);
        this.isLoadingMachineDetails = false;
        this.loadingIndicator.hide();
      },
      error: error => {
        this.isLoadingMachineDetails = false;
        this.displayErrorGettingMachine(error);
      }
    });
  }

  private getMachineTagDetails(category: string, tag: string) {
    if (!tag || !category || tag.isNullOrWhitespace() || category.isNullOrWhitespace()) {
      this.machineTag = null;
      return;
    }

    this.machineTagService.getTag(category, tag).pipe(takeUntil(this.destroy)).subscribe({
      next: (machineTag: MachineTag) => {
        this.machineTag = machineTag;
      }, error: (error) => {
        const message = this.translate.instant('Error getting machine tag configuration.');
        this.notification.error(message, error, this.CURRENT_PAGE_NAME, true);
        this.machineTag = null;
      }
    });
  }

  private displayErrorGettingMachine(error): void {
    console.log(error);
    const message = this.translate.instant('Error getting machine configuration.');
    this.notification.error(message, error, this.CURRENT_PAGE_NAME, true);
    this.loadingIndicator.hide();
  }

  private loadSignalsData(sig: any): Signal[] {
    if (!sig) {
      return [];
    }
    let signals: Signal[] = [];
    const signalsKeys = Object.keys(sig);
    signalsKeys.forEach(element => {
      signals.push(sig[element]);
    });
    signals = signals.sort((a, b) => this.sortingService.sortByName(a, b));
    return signals;
  }


  ngOnDestroy(): void {
    this.destroy.next(true);
    this.destroy.complete();
  }

  protected readonly ELASTIC_SEARCH_TYPE = ELASTIC_SEARCH_TYPE;
}
